import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import { formatNumberWithAbbreviation } from "../../utils/utils";
import type { MetricCardData } from "./types";

interface MetricCardProps extends MetricCardData {
  expanded: boolean;
  onToggle: () => void;
}

export default function MetricCard(props: MetricCardProps) {
  const {
    agent,
    displayName,
    color,
    iconUrl,
    primaryValue,
    description,
    expanded,
    onToggle,
  } = props;
  const theme = useTheme();

  // Get icon path - use iconUrl if available, otherwise construct from agent name
  const getIconPath = () => {
    if (iconUrl) {
      return iconUrl;
    }
    const agentName = agent.toLowerCase();
    return `/static/images/agent-icons/${agentName}.svg`;
  };

  return (
    <Paper
      elevation={0}
      data-testid={`metric-card-${agent}`}
      sx={{
        width: "100%",
        borderRadius: "8px",
        overflow: "visible",
        backgroundColor: color,
        boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
        display: "flex",
        flexDirection: "column",
        alignSelf: "flex-start",
      }}
    >
      {/* Header with agent name and icon */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          padding: "12px 20px 8px",
          gap: "16px",
          backgroundColor: color,
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Inter Bold",
            fontStyle: "normal",
            fontWeight: 700,
            fontSize: "18px",
            lineHeight: "28px",
            letterSpacing: "0.0175em",
            color: theme.palette.primary.main,
            flex: "none",
            order: 0,
            flexGrow: 1,
          }}
        >
          {displayName}
        </Typography>
        <Box
          component="img"
          src={getIconPath()}
          alt={`${displayName} icon`}
          sx={{
            width: "23.35px",
            height: "23.35px",
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Content area */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "20px 20px 12px",
          gap: "8px",
          backgroundColor: "#FFFFFF",
          border: "1px solid rgba(208, 213, 221, 0.5)",
          boxShadow: "0px 1px 2px rgba(10, 13, 18, 0.05)",
          borderRadius: "8px",
        }}
      >
        {/* Number and description */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: "0px",
            gap: "12px",
            alignSelf: "stretch",
          }}
        >
          {/* Primary value */}
          <Typography
            sx={{
              fontFamily: "Inter Bold",
              fontStyle: "normal",
              fontWeight: 700,
              fontSize: "30px",
              lineHeight: "38px",
              letterSpacing: "0.0237em",
              color: theme.palette.primary.main,
              flex: "none",
              order: 0,
              flexGrow: 0,
            }}
          >
            {formatNumberWithAbbreviation(primaryValue, 999999)}
          </Typography>

          {/* Description lines */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              padding: "0px",
            }}
          >
            {description.map((line, index) => (
              <Typography
                key={line}
                sx={{
                  fontFamily: index === 0 ? "Inter Bold" : "Inter Regular",
                  fontStyle: "normal",
                  fontWeight: index === 0 ? 700 : 500,
                  fontSize: index === 0 ? "18px" : "14px",
                  lineHeight: index === 0 ? "28px" : "20px",
                  letterSpacing: index === 0 ? "0.0175em" : "0.0125em",
                  color: index === 0 ? theme.palette.success.main : "#475467",
                  textAlign: index === 0 ? "center" : undefined,
                  flex: "none",
                  order: index === 0 ? 0 : 1,
                  flexGrow: 0,
                }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        </Box>

        {/* Expand/Collapse button */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            padding: "0px",
          }}
        >
          <Tooltip title="Practice Breakdown" placement="top" arrow>
            <IconButton
              size="small"
              onClick={onToggle}
              sx={{
                width: "24px",
                height: "24px",
                padding: 0,
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: `${theme.palette.primary.main}0A`,
                },
              }}
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Paper>
  );
}
